import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LangEnum } from '@/lang/enum';
import { formatDate, formatPercentage, formatTimeAgo, formatValueWithCompactNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store';
import TokenAvatar from '@/widget/TokenAvatar';
import BigNumber from 'bignumber.js';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
interface IProps {
  noticeInfo: {
    address: string;
    highestPrice: string;
    firstAlertPrice: string;
    logo: string;
    createTime: number;
    symbol: string;
    totalSupply: number;
  };
  noticeList: { price: string; pushTimes: number; timestamp: number }[];
}
export default (props: IProps) => {
  const { noticeList, noticeInfo } = props;
  const { t } = useTranslation();
  const { fwClass, lang } = useAppStore();

  const [open, setOpen] = useState(false);
  const isHasData = noticeInfo?.address.length > 0;

  const priceChg = isHasData ? BigNumber(noticeInfo?.highestPrice).minus(noticeInfo?.firstAlertPrice).div(noticeInfo?.firstAlertPrice).toString() : 0;
  const multiple = BigNumber(priceChg).lt(1) ? `<1` : priceChg.toString().split('.')[0];

  const text = `${multiple}x(${formatPercentage(priceChg.toString(), { scale: true })})`;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        <div
          className={cn(
            'bg-[url(/icon/multiple.svg)] bg-cover bg-no-repeat w-[70px] h-[50px] text-center text-[20px] text-themeColor pt-[20px]',
            fwClass,
          )}
        >
          {multiple}x
        </div>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="p-[16px] border-t-[1px]  border-t-borderColor rounded-tl-[12px] rounded-tr-[12px] outline-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="hidden" />
        <SheetTitle className="hidden" />
        <SheetDescription className="hidden" />
        <div className="flex flex-col gap-y-[16px] h-[500px] overflow-hidden">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-[12px]">
              <TokenAvatar className="size-[25px]" address={noticeInfo?.address} logo={noticeInfo?.logo} />
              <div>
                <div>{noticeInfo?.symbol}</div>
                <span className="text-secondaryTextColor text-[12px]">{formatTimeAgo(noticeInfo?.createTime, { style: 'shortAgo' })}</span>
              </div>
            </div>
            <X className="size-[16px]" onClick={() => setOpen(false)} />
          </div>
          <div className="text-[14px]">
            {lang == LangEnum.EN ? (
              <p>
                The maximum increase after the first alert was <span className={cn('text-[16px] text-themeColor', fwClass)}>{text}</span>
              </p>
            ) : lang == LangEnum.ZH_CN ? (
              <p>
                第一次提醒后的最大增幅为<span className={cn('text-[16px] text-themeColor', fwClass)}>{text}</span>
              </p>
            ) : (
              <p>
                第一次提醒後的最大增幅為<span className={cn('text-[16px] text-themeColor', fwClass)}>{text}</span>
              </p>
            )}
          </div>
          <Table className="w-full" wrapperClassName="text-[12px]">
            <TableHeader>
              <TableRow className="border-none text-[12px]">
                <TableHead className="w-[150px] px-0">{t('Signal.time')}</TableHead>
                <TableHead className="w-[96px] px-0">{t('Signal.alert')}</TableHead>
                <TableHead className="w-auto px-0">{t('Signal.mc')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {noticeList?.length > 0 &&
                noticeList.map((item, index) => {
                  const mc = BigNumber(item.price).times(noticeInfo.totalSupply).toString();
                  const time = formatDate(item.timestamp * 1000).replace(/^\d{4}\//, '');
                  return (
                    <TableRow className="h-[64px] !border-t-[1px] border-borderColor" key={index}>
                      <TableCell>{time}</TableCell>
                      <TableCell>{noticeList?.length - index}</TableCell>
                      <TableCell>{formatValueWithCompactNumber(mc, true)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </SheetContent>
    </Sheet>
  );
};
